import { motion } from "framer-motion";
import {
  ArrowRight,
  Play,
  CheckCircle2,
  BarChart3,
  Zap,
  Shield,
  LogIn,
} from "lucide-react";
import Button from "../UI/Button";
import { useInView } from "../../hooks/useAnimations";
import heroimage from "../../assets/avatarlogo/heroimage.png";
import { Link } from "react-router-dom";

// Trust Badge Component
const TrustBadge = ({ icon: Icon, text }) => (
  <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/80 border border-purple-100 shadow-sm">
    <Icon className="w-4 h-4 text-purple-600" />
    <span className="text-xs font-medium text-slate-700">{text}</span>
  </div>
);

const Hero = ({ onBookDemo }) => {
  const [ref, isInView] = useInView({ threshold: 0.1 });

  return (
    <section
      ref={ref}
      className="relative min-h-screen flex items-center pt-20 pb-32 overflow-hidden bg-white"
    >
      {/* Background Decorations */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Gradient Orbs */}
        <motion.div
          animate={{ x: [0, 30, 0], y: [0, -20, 0] }}
          transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
          className="absolute top-1/4 left-1/4 w-[400px] h-[400px] rounded-full orb-purple"
        />
        <motion.div
          animate={{ x: [0, -40, 0], y: [0, 30, 0] }}
          transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
          className="absolute bottom-1/4 right-1/4 w-[500px] h-[500px] rounded-full orb-indigo"
        />

        {/* Grid Pattern */}
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `radial-gradient(circle at 1px 1px, rgba(124, 58, 237, 1) 1px, transparent 0)`,
            backgroundSize: "40px 40px",
          }}
        />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-center lg:text-left"
          >
            {/* Main Headline */}
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="text-3xl sm:text-4xl md:text-5xl lg:text-5xl font-bold text-slate-900 leading-tight mb-6"
              style={{ fontFamily: '"Playfair Display", Georgia, serif' }}
            >
              Manage Calls, Leads & Teams
              <span className="text-gradient"> All in One Smart Platform </span>
            </motion.h1>

            {/* Subheadline */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="text-lg md:text-xl text-slate-600 max-w-2xl mx-auto lg:mx-0 mb-8 leading-relaxed"
            >
              Built for telecalling teams with lead tracking, attendance, call
              monitoring, reports, and automation. Everything you need to scale
              your operations.
            </motion.p>

            {/* CTA Buttons */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4 mb-10"
            >
              <Button
                variant="primary"
                size="lg"
                onClick={onBookDemo}
                icon={ArrowRight}
                iconPosition="right"
                className="min-w-[180px] shadow-xl shadow-purple-500/30"
              >
                Book Demo
              </Button>
              <Link to="/login">
                <Button
                  variant="secondary"
                  size="lg"
                  icon={LogIn}
                  className="min-w-[180px]"
                >
                  Login
                </Button>
              </Link>
            </motion.div>

            {/* Trust Badges */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="flex flex-wrap items-center justify-center lg:justify-start gap-3"
            >
              <TrustBadge icon={CheckCircle2} text="No credit card required" />
              <TrustBadge icon={Shield} text="14-day free trial" />
              <TrustBadge icon={Zap} text="Setup in minutes" />
            </motion.div>
          </motion.div>

          {/* Right - Device Showcase */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="relative hidden lg:block mt-8"
          >
            {/* Main Dashboard Mockup */}
            <div className="relative">
              {/* Dashboard Image */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.5 }}
                className="relative"
              >
                <img
                  src={heroimage}
                  alt="TeleValley Dashboard"
                  className="w-full h-auto object-cover"
                />
              </motion.div>

              {/* Floating Analytics Card */}
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={isInView ? { opacity: 1, scale: 1 } : {}}
                transition={{ delay: 0.8 }}
                className="absolute -bottom-6 -left-12 bg-white rounded-xl shadow-xl shadow-purple-500/10 border border-purple-100 p-4 w-56"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-500 to-indigo-500 flex items-center justify-center">
                    <BarChart3 className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-slate-900">
                      Real-time Analytics
                    </div>
                    <div className="text-xs text-slate-500">Live updates</div>
                  </div>
                </div>
                <div className="flex items-end gap-1 h-12">
                  {[40, 65, 45, 80, 55, 90, 70].map((height, i) => (
                    <motion.div
                      key={i}
                      initial={{ height: 0 }}
                      animate={isInView ? { height: `${height}%` } : {}}
                      transition={{ delay: 1 + i * 0.1, duration: 0.5 }}
                      className="flex-1 bg-gradient-to-t from-purple-500 to-indigo-400 rounded-t"
                    />
                  ))}
                </div>
              </motion.div>

              {/* Floating Lead Card */}
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={isInView ? { opacity: 1, scale: 1 } : {}}
                transition={{ delay: 0.9 }}
                className="absolute -top-4 -right-8 bg-white rounded-xl shadow-xl shadow-purple-500/10 border border-purple-100 p-3"
              >
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-green-400 to-emerald-500 flex items-center justify-center">
                    <CheckCircle2 className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <div className="text-xs font-medium text-slate-900">
                      New Lead Added
                    </div>
                    <div className="text-xs text-slate-500">Just now</div>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
